import React, { useState } from 'react';
import { Dialog } from '@headlessui/react';
import { Budget, CategoryItem, ResponsibleItem, DestinationItem } from '../types/budget';
import { useBudgetEntities } from '../hooks/useBudgetEntities';
import { PencilIcon, TrashIcon, PlusIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

interface EntitiesManagementDialogProps {
  isOpen: boolean;
  onClose: () => void;
  budget: Budget;
}

export const EntitiesManagementDialog: React.FC<EntitiesManagementDialogProps> = ({
  isOpen,
  onClose,
  budget,
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'categories' | 'responsibles' | 'destinations'>(
    'categories'
  );
  const [newItemName, setNewItemName] = useState('');
  const [editingItem, setEditingItem] = useState<{ id: string; name: string } | null>(null);

  const {
    addCategory,
    addResponsible,
    addDestination,
    updateCategory,
    updateResponsible,
    updateDestination,
    deleteCategory,
    deleteResponsible,
    deleteDestination,
  } = useBudgetEntities(budget.id || null);

  const handleAddItem = () => {
    if (!newItemName.trim()) return;

    if (activeTab === 'categories') {
      addCategory(newItemName);
    } else if (activeTab === 'responsibles') {
      addResponsible(newItemName);
    } else {
      addDestination(newItemName);
    }

    setNewItemName('');
  };

  const handleUpdateItem = (id: string, newName: string) => {
    if (!newName.trim()) return;

    if (activeTab === 'categories') {
      updateCategory(id, newName);
    } else if (activeTab === 'responsibles') {
      updateResponsible(id, newName);
    } else {
      updateDestination(id, newName);
    }

    setEditingItem(null);
  };

  const handleDeleteItem = (id: string) => {
    if (activeTab === 'categories') {
      deleteCategory(id);
    } else if (activeTab === 'responsibles') {
      deleteResponsible(id);
    } else {
      deleteDestination(id);
    }
  };

  const isItemProtected = (item: CategoryItem | ResponsibleItem | DestinationItem): boolean => {
    if (activeTab === 'responsibles') {
      return !!(item as ResponsibleItem).isProtected;
    } else if (activeTab === 'destinations') {
      return !!(item as DestinationItem).isProtected;
    }
    return false;
  };

  const renderItems = () => {
    let items;
    if (activeTab === 'categories') {
      items = budget.categories;
    } else if (activeTab === 'responsibles') {
      items = budget.responsibles;
    } else {
      items = budget.destinations;
    }

    if (items.length === 0) {
      return <div className="text-gray-500 dark:text-gray-400">{t('entities.noItems')}</div>;
    }

    return items.map((item) => (
      <div
        key={item.id}
        className="mb-2 flex items-center justify-between rounded-md border border-gray-200 p-3 dark:border-gray-700"
      >
        {editingItem && editingItem.id === item.id ? (
          <div className="flex w-full items-center gap-2">
            <input
              type="text"
              value={editingItem.name}
              onChange={(e) => setEditingItem({ ...editingItem, name: e.target.value })}
              className="flex-1 rounded-md border border-gray-300 p-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              placeholder={
                activeTab === 'categories'
                  ? t('entities.new.category')
                  : activeTab === 'responsibles'
                    ? t('entities.new.responsible')
                    : t('entities.new.destination')
              }
            />
            <button
              onClick={() => handleUpdateItem(item.id, editingItem.name)}
              className="rounded-md bg-indigo-600 px-3 py-2 text-white hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600"
            >
              {t('common.save')}
            </button>
            <button
              onClick={() => setEditingItem(null)}
              className="rounded-md bg-gray-300 px-3 py-2 text-gray-700 hover:bg-gray-400 dark:bg-gray-600 dark:text-gray-200 dark:hover:bg-gray-500"
            >
              {t('common.cancel')}
            </button>
          </div>
        ) : (
          <>
            <span className="flex-1 text-gray-700 dark:text-gray-200">{item.name}</span>
            <div className="flex gap-2">
              <button
                onClick={() => setEditingItem({ id: item.id, name: item.name })}
                disabled={isItemProtected(item)}
                className={`rounded p-1 text-indigo-500 hover:bg-indigo-50 hover:text-indigo-700 dark:text-indigo-400 dark:hover:bg-indigo-900/20 dark:hover:text-indigo-300 ${
                  isItemProtected(item) ? 'cursor-not-allowed opacity-50' : ''
                }`}
                title={isItemProtected(item) ? t('entities.protected') : t('common.edit')}
              >
                <PencilIcon className="h-5 w-5" />
              </button>
              <button
                onClick={() => handleDeleteItem(item.id)}
                disabled={isItemProtected(item)}
                className={`rounded p-1 text-red-500 hover:bg-red-50 hover:text-red-700 dark:text-red-400 dark:hover:bg-red-900/20 dark:hover:text-red-300 ${
                  isItemProtected(item) ? 'cursor-not-allowed opacity-50' : ''
                }`}
                title={isItemProtected(item) ? t('entities.protected') : t('common.delete')}
              >
                <TrashIcon className="h-5 w-5" />
              </button>
            </div>
          </>
        )}
      </div>
    ));
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className="fixed inset-0 z-10 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center">
        <div
          className="fixed inset-0 bg-black bg-opacity-30 dark:bg-opacity-50"
          aria-hidden="true"
        />

        <div className="relative mx-4 w-full max-w-lg rounded-lg bg-white p-6 shadow-xl dark:bg-gray-800">
          <Dialog.Title className="mb-4 text-lg font-medium text-gray-900 dark:text-white">
            {t('entities.management')}
          </Dialog.Title>

          <div className="mb-6">
            <div className="mb-4 border-b border-gray-200 dark:border-gray-700">
              <ul className="-mb-px flex">
                <li className="mr-2">
                  <button
                    onClick={() => setActiveTab('categories')}
                    className={`inline-block rounded-t-lg border-b-2 p-4 ${
                      activeTab === 'categories'
                        ? 'border-indigo-600 text-indigo-600 dark:border-indigo-500 dark:text-indigo-500'
                        : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-300'
                    }`}
                  >
                    {t('entities.categories')}
                  </button>
                </li>
                <li className="mr-2">
                  <button
                    onClick={() => setActiveTab('responsibles')}
                    className={`inline-block rounded-t-lg border-b-2 p-4 ${
                      activeTab === 'responsibles'
                        ? 'border-indigo-600 text-indigo-600 dark:border-indigo-500 dark:text-indigo-500'
                        : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-300'
                    }`}
                  >
                    {t('entities.responsibles')}
                  </button>
                </li>
                <li className="mr-2">
                  <button
                    onClick={() => setActiveTab('destinations')}
                    className={`inline-block rounded-t-lg border-b-2 p-4 ${
                      activeTab === 'destinations'
                        ? 'border-indigo-600 text-indigo-600 dark:border-indigo-500 dark:text-indigo-500'
                        : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-300'
                    }`}
                  >
                    {t('entities.destinations')}
                  </button>
                </li>
              </ul>
            </div>

            <div className="mb-4 flex items-center gap-2">
              <input
                type="text"
                value={newItemName}
                onChange={(e) => setNewItemName(e.target.value)}
                className="flex-1 rounded-md border border-gray-300 p-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                placeholder={
                  activeTab === 'categories'
                    ? t('entities.new.category')
                    : activeTab === 'responsibles'
                      ? t('entities.new.responsible')
                      : t('entities.new.destination')
                }
              />
              <button
                onClick={handleAddItem}
                className="flex items-center gap-1 rounded-md bg-indigo-600 px-3 py-2 text-white hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600"
              >
                <PlusIcon className="h-5 w-5" />
                <span>{t('common.add')}</span>
              </button>
            </div>

            <div className="h-[300px] overflow-y-auto">{renderItems()}</div>
          </div>

          <div className="mt-6 flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
            >
              {t('common.close')}
            </button>
          </div>
        </div>
      </div>
    </Dialog>
  );
};
